'use client';

import { useAuth } from '@clerk/clerk-react';
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { apiClient } from '../api/client';
import { Member, MembersResponse } from './useOrganizationMembers';

interface RemoveMemberResponse {
  message: string;
  memberId: string;
}

/**
 * Hook for removing a member from the organization
 * DELETE /api/v1/organization/members/{memberId}
 *
 * Uses optimistic updates with rollback on error
 */
export function useRemoveMember() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    RemoveMemberResponse,
    Error,
    string,
    { previousMembers: InfiniteData<MembersResponse> | undefined }
  >({
    mutationFn: async (memberId: string) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.DELETE(
        '/api/v1/organization/members/{memberId}',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            path: {
              memberId: memberId,
            },
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to remove member');
      }

      return {
        message: data.message,
        memberId: data.memberId,
      };
    },
    onMutate: async (memberId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['organization-members'] });

      // Snapshot the previous value
      const previousMembers = queryClient.getQueryData<
        InfiniteData<MembersResponse>
      >(['organization-members']);

      // Optimistically drop the member from every loaded page and decrement the
      // grand total each page carries.
      queryClient.setQueryData<InfiniteData<MembersResponse>>(
        ['organization-members'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              members: page.members.filter((m: Member) => m.id !== memberId),
              count: page.count - 1,
            })),
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousMembers };
    },
    onError: (_err, _memberId, context) => {
      // Rollback to the previous value on error
      if (context?.previousMembers) {
        queryClient.setQueryData(
          ['organization-members'],
          context.previousMembers
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}
